import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UtilsService } from '@horizon/core';
import { HorizonButtonComponent } from '@horizon/core-buttons';
import { ItemContext } from '@horizon/dm-core';
import { ContactDetailsItem, GenesysData } from '@shared/interfaces/CallLog';
import { ApiExceptionsHandlerService } from '@shared/services/api-exceptions-handler.service';
import { DashboardApiService } from '@shared/services/dashboard-api.service';
import { HorizonGenesysCallLogService } from '@shared/services/horizon-genesys-call-log.service';
import { MonitorAccessApiService } from '@shared/services/monitor-access-api.service';
import { StorageApiService } from '@shared/services/storage-api.service';
import {
  catchError,
  filter,
  finalize,
  map,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { globalSettings } from 'src/global-settings';
import { CallLogGridConfigService } from '../../config/call-log-grid-config.service';
import { layout } from '../../config/layout';
import {
  OpenCallLogReportEmitter,
  TableData,
  TableTemplate,
} from '../../models';

@Component({
  selector: 'call-log-search-view',
  templateUrl: './call-log-search-view.component.html',
  styleUrls: ['./call-log-search-view.component.scss'],
})
export class CallSearchViewComponent implements OnInit, OnDestroy {
  // #region Inputs & Outputs
  @Input() genesysData: GenesysData;
  @Output() openCallLogReport = new EventEmitter<OpenCallLogReportEmitter>();
  // #endregion

  // #region ViewChild
  @ViewChild('searchButton') searchButton: HorizonButtonComponent;
  @ViewChild('openCallLogButton') openCallLogButton: HorizonButtonComponent;
  // #endregion

  // #region Public Properties
  globalSettings = globalSettings;
  searchForm: FormGroup;

  users: any;

  accountId: number;
  customerId: number;
  customerDisplayName: string;

  isCallerSelected = false;
  isKnowCaller = false;
  preselectFirstRow = false;

  // -- STATIC GRIDS --
  customerData: Array<TableData>;
  customerTemplate = this.utils.getLayoutElementById(
    layout,
    'customer-call-log-search-list-columns'
  ).elements;

  recentCallsData: Array<TableData>;
  recentCallsTemplate: TableTemplate = this.utils.getLayoutElementById(
    layout,
    'recent-calls-call-log-search-list-columns'
  ).elements;

  // -- DYNAMIC GRIDS --
  resultsData: Array<any> = [];
  resultsTemplate = this.utils.getLayoutElementById(
    layout,
    'results-call-log-search-list-columns'
  ).elements;

  accountData: Array<any> = [];
  accountTemplate = this.utils.getLayoutElementById(
    layout,
    'account-call-log-search-list-columns'
  ).elements;

  thirdPartyData: Array<any> = [];
  thirdPartyTemplate = this.utils.getLayoutElementById(
    layout,
    'third-party-call-log-search-list-columns'
  ).elements;

  // FORM / MODE VARIABLES
  context: ItemContext;
  isCreatingMode = false;
  scopeId = -1;

  isUnauthorised = false;
  isThirdPartyCall = false;
  isThirdPartyPanel = false;
  isThirdPartySelected = false;
  accountSelected = false;
  thirdPartySelected = false;
  // #endregion

  // #region Private Properties
  private isTransferred = false;
  private relationship: string;
  private readonly destroy$ = new Subject<void>();
  // #endregion

  // #region Constructor
  constructor(
    readonly callLogGridConfigService: CallLogGridConfigService,
    private readonly apiExceptionsHandlerService: ApiExceptionsHandlerService,
    private readonly callLogService: HorizonGenesysCallLogService,
    private readonly dashboardApiService: DashboardApiService,
    private readonly fb: FormBuilder,
    private readonly monitorAccessService: MonitorAccessApiService,
    private readonly storageApiService: StorageApiService,
    private readonly utils: UtilsService
  ) {
    this.initializeUsersStream();
    this.buildForm();
  }
  // #endregion

  // #region Lifecycle Hooks
  ngOnInit(): void {
    this.initializeSearchView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  // #endregion

  // #region UI Events - Table Selection
  onCustomersRowActionEvent(e): void {
    this.setCustomerData(e.rowData);
    this.fetchCallLogReport();
  }

  onSelectAccountsRowActionEvent(e): void {
    this.accountSelected = true;
    this.accountId = e.rowData.Id;
    this.updateCallerSelected();
  }

  onDeselectAccountsRowActionEvent(e): void {
    this.accountSelected = false;
    this.updateCallerSelected();
  }

  onSelectThirdPartiesRowActionEvent(e): void {
    this.thirdPartySelected = true;
    this.relationship = 'ThirdParty';
    this.customerId = e.rowData.Id;
    this.updateCallerSelected();
  }

  onDeselectThirdPartiesRowActionEvent(e): void {
    this.thirdPartySelected = false;
    this.relationship = '';
    this.updateCallerSelected();
  }
  // #endregion

  // #region UI Events - Form
  onSubmit(): void {
    this.isThirdPartyPanel
      ? this.getThirdPartyAndAccounts()
      : this.getResults();
  }

  onUnauthorisedChange(value?: boolean): void {
    this.isUnauthorised = value ?? !this.isUnauthorised;
    this.callLogService.setIsUnauthorised(this.isUnauthorised);

    if (this.isUnauthorised) this.on3rdPartyPanelChange(false);
  }

  on3rdPartyPanelChange(value?: boolean): void {
    this.isThirdPartyPanel = value ?? !this.isThirdPartyPanel;
    this.customerData = [];
    this.isCallerSelected = false;

    this.callLogService.setIsThirdPartyPanel(this.isThirdPartyPanel);

    if (this.isThirdPartyPanel) this.onUnauthorisedChange(false);
  }

  onTransferChange(): void {
    this.isTransferred = !this.isTransferred;
    this.callLogService.setIsTransferred(this.isTransferred);
  }
  // #endregion

  // #region Public Methods
  openCallLog(): void {
    const callLogReportData: OpenCallLogReportEmitter = {
      isCallLogReportView: true,
      accountId: this.accountId,
      customerId: this.customerId,
      callType: this.genesysData.callType,
      callSubtype: this.genesysData.callSubType,
      relationship: this.relationship,
    };

    this.openCallLogReport.emit(callLogReportData);
  }
  // #endregion

  // #region Private Methods - Init
  private initializeUsersStream(): void {
    this.monitorAccessService
      .getAllUsersChanges()
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap((users) => (this.users = users))
      )
      .subscribe();
  }

  private buildForm(): void {
    this.searchForm = this.fb.group({
      name: [''],
      accountNumber: [''],
    });
  }

  private initializeSearchView() {
    if (this.genesysData.accountNumber) {
      this.searchForm
        .get('accountNumber')
        ?.patchValue(this.genesysData.accountNumber);
      this.getResults();
      return;
    }
    this.isThirdPartyCall = this.storageApiService.getLocalStorageData(
      'thirdPartyCall'
    ) as boolean;
    if (this.isThirdPartyCall) {
      this.on3rdPartyPanelChange(true);
      const thirdPartyCalledData = this.storageApiService.getLocalStorageData(
        'thirdPartyCalledData'
      ) as ContactDetailsItem;
      this.searchForm.controls['name'].setValue(thirdPartyCalledData.name);
      this.searchThirdParty(thirdPartyCalledData.name);
    } else {
      this.searchByTelephoneNumber();
    }
  }
  // #endregion

  // #region Private Methods - Searches
  private getResults(): void {
    const { name, accountNumber } = this.searchForm.controls;

    this.callLogService
      .searchResults(undefined, name.value, accountNumber.value)
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap((res) => this.handleSubmitResponse(res)),
        catchError((error) =>
          this.apiExceptionsHandlerService.catchErrorHandler(error, 'Call Log')
        ),
        finalize(() => this.searchButton.stopLoading())
      )
      .subscribe();
  }

  private getThirdPartyAndAccounts(): void {
    const { name, accountNumber } = this.searchForm.controls;
    this.searchThirdParty(name.value);
    this.searchAccountReference(accountNumber.value);
  }

  private searchThirdParty(name: string): void {
    this.callLogService
      .searchThirdParty(name)
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        map((thirdParties) => thirdParties.map((tp) => tp.fields)),
        tap((mapped) => (this.thirdPartyData = mapped)),
        catchError((err) =>
          this.apiExceptionsHandlerService.catchErrorHandler(err, 'call-log')
        ),
        finalize(() => this.searchButton.stopLoading())
      )
      .subscribe();
  }

  private searchAccountReference(accountRef: string): void {
    this.callLogService
      .searchAccount(accountRef)
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        map((accounts) => accounts.map((a) => a.fields)),
        tap((mapped) => (this.accountData = mapped)),
        catchError((err) =>
          this.apiExceptionsHandlerService.catchErrorHandler(err, 'call-log')
        ),
        finalize(() => this.searchButton.stopLoading())
      )
      .subscribe();
  }

  private searchByTelephoneNumber(): void {
    const transformedPhone = this.genesysData.phoneNumber.replace('+', '%2b');

    this.callLogService
      .searchByTelephoneNumber(transformedPhone)
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap((response) => this.handleSearchResponse(response)),
        finalize(() => this.handleSearchComplete()),
        catchError((error) =>
          this.apiExceptionsHandlerService.catchErrorHandler(error, 'Call Log')
        )
      )
      .subscribe();
  }
  // #endregion

  // #region Private Methods - Data Handling
  private setCustomerData(rowData: any): void {
    this.customerData = [rowData];
    this.customerId = rowData.PartyId;
    this.accountId = rowData.AccountId;
    this.customerDisplayName = rowData.CustomerName;
    this.relationship = rowData.Relationship;
  }

  private fetchCallLogReport(): void {
    this.dashboardApiService
      .getCallLogReport(this.customerId, this.accountId)
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap((report) => this.prepareDataForResults(report)),
        finalize(() => {
          this.isCallerSelected = true;
          this.openCallLogButton.stopLoading();
        }),
        catchError((err) =>
          this.apiExceptionsHandlerService.catchErrorHandler(err, 'Dashboard')
        )
      )
      .subscribe();
  }

  private prepareDataForResults(resp: any): void {
    this.recentCallsData = resp.data?.rows?.length
      ? resp.data.rows.map(this.transformRow)
      : [];
  }

  private transformRow(row: any): any {
    return Object.keys(row).reduce((acc, key) => {
      acc[key] = row[key].value;
      return acc;
    }, {});
  }

  private handleSearchResponse(response: any): void {
    this.resultsData = response;
    this.isKnowCaller = response.length > 0;
  }

  private handleSearchComplete(): void {
    this.preselectFirstRow = this.resultsData.length === 1;
  }

  private handleSubmitResponse(response: any): void {
    this.resultsData = response;
    this.isKnowCaller = response.length > 0;
  }
  // #endregion

  // #region Private Helpers
  private updateCallerSelected(): void {
    this.isCallerSelected = this.accountSelected && this.thirdPartySelected;
  }
  // #endregion
}
